/**
 * Die Rückfrage vor dem Aufteilen eines Platzes.
 *
 * Gezeigt wird, was gemessen wurde: die Gruppen aus den GPS-Punkten der
 * Tauchgänge, jede mit ihrer Position, ihrem Zeitraum und ihren Nummern. Wer
 * dort getaucht ist, erkennt daran sofort, welche Gruppe wohin gehört — die
 * Software kann das nicht wissen.
 *
 * Angehakt wird die Gruppe, die **wegzieht**. Der neue Name gehört zu ihr; der
 * bleibende Satz behält seinen. Vorausgewählt ist die kleinere Gruppe, weil
 * dann weniger Tauchgänge umhängen — überstimmen kann man das.
 *
 * Das Aufräumen folgt dem Sammelformular: drei Wege ans Ende, ein Riegel
 * dagegen, dass sie sich in die Quere kommen. Der Grund steht dort
 * ausführlich — `close` kommt nicht auf jeder Plattform an.
 */

import { koordinaten } from '../format.js';
import { gruppiere, plan } from '../aufteilen.js';
import { tauchgangszahl } from '../verortung.js';
import { alsBlatt } from './blatt.js';

const jahr = (tg) => String(tg.start_utc ?? '').slice(0, 4);

function gruppentext(gruppe) {
  const jahre = gruppe.tauchgaenge.map(jahr).filter(Boolean).sort();
  const zeitraum = jahre.length === 0 ? '—'
    : (jahre[0] === jahre[jahre.length - 1] ? jahre[0] : `${jahre[0]}–${jahre[jahre.length - 1]}`);
  const nummern = gruppe.tauchgaenge.map((tg) => tg.dive_number)
    .filter((n) => n !== null && n !== undefined);
  return {
    ort: gruppe.ohneGps ? 'ohne GPS' : koordinaten(gruppe.lat, gruppe.lon),
    zeitraum,
    nummern: nummern.length ? `Nr. ${nummern.join(', ')}` : 'ohne Nummer',
    anzahl: gruppe.tauchgaenge.length,
  };
}

/**
 * Fragt, welche Tauchgänge wegziehen und wie der neue Platz heisst.
 * Zurück kommt `{ ids, name }` oder `null`.
 */
export function frageAufteilen(platz, tauchgaenge) {
  const gruppen = gruppiere(tauchgaenge);

  return new Promise((fertig) => {
    const dialog = document.createElement('dialog');
    dialog.className = 'sammelformular aufteildialog';
    // Vorausgewählt ist die kleinste Gruppe: Sie umzuhängen ist der kleinere
    // Eingriff, und bei einer Kollision ist der Zuzug meist der jüngere.
    const kleinste = gruppen.length > 1
      ? gruppen.reduce((a, b) => (b.tauchgaenge.length < a.tauchgaenge.length ? b : a))
      : null;

    dialog.innerHTML = `
      <form method="dialog">
        <h2>Platz aufteilen</h2>
        <p class="hinweis">„${platz.name}" trägt Tauchgänge von
          ${gruppen.length} Orten. Häkchen setzen bei allem, was <strong>wegzieht</strong> —
          der Rest bleibt unter dem alten Namen.</p>
        <div class="aufteilgruppen">
          ${gruppen.map((gruppe, i) => {
    const t = gruppentext(gruppe);
    return `<label class="aufteilgruppe${gruppe === kleinste ? ' gewaehlt' : ''}">
              <input type="checkbox" data-gruppe="${i}"${gruppe === kleinste ? ' checked' : ''}>
              <span class="aufteilort">${t.ort}</span>
              <span class="aufteilzeit">${t.zeitraum} · ${t.anzahl} ${
  t.anzahl === 1 ? 'Tauchgang' : 'Tauchgänge'}</span>
              <span class="aufteilnummern">${t.nummern}</span>
            </label>`;
  }).join('')}
        </div>
        <label class="aufteilname">Name des neuen Platzes
          <input type="text" class="neuername" value="${platz.name} (2)">
        </label>
        <label class="aufteilleeren" hidden>
          <input type="checkbox" class="leeren" checked>
          <span></span>
        </label>
        <p class="wirkung"></p>
        <p class="meldung" role="status" aria-live="polite"></p>
        <div class="dialogfuss">
          <button type="button" class="knopf abbrechen">Abbrechen</button>
          <button type="button" class="knopf aufteilen">Aufteilen</button>
        </div>
      </form>`;
    document.body.append(dialog);

    const wirkung = dialog.querySelector('.wirkung');
    const namensfeld = dialog.querySelector('.neuername');
    const knopf = dialog.querySelector('.aufteilen');

    const gewaehlteIds = () => [...dialog.querySelectorAll('input[data-gruppe]:checked')]
      .flatMap((h) => gruppen[Number(h.dataset.gruppe)].tauchgaenge.map((tg) => tg.id));

    function zeigeWirkung() {
      for (const feld of dialog.querySelectorAll('.aufteilgruppe')) {
        feld.classList.toggle('gewaehlt', feld.querySelector('input').checked);
      }
      const ids = gewaehlteIds();
      const ergebnis = plan(platz, tauchgaenge, ids);
      knopf.disabled = !ergebnis.machbar;
      if (!ergebnis.machbar) {
        wirkung.textContent = ids.length === 0
          ? 'Nichts angehakt — es gibt nichts umzuhängen.'
          : 'Alles angehakt — dann bliebe der alte Platz ohne Tauchgänge stehen.';
        return;
      }
      const name = namensfeld.value.trim() || 'der neue Platz';
      const zieht = ergebnis.umziehend.length === 1 ? 'zieht' : 'ziehen';
      const bleibt = ergebnis.bleibend.length === 1 ? 'bleibt' : 'bleiben';
      wirkung.textContent = `${tauchgangszahl(ergebnis.umziehend.length)} ${zieht} zu „${name}"`
        + (ergebnis.neuerOrt
          ? ` (${koordinaten(ergebnis.neuerOrt.lat, ergebnis.neuerOrt.lon)})` : ' (ohne Position)')
        + `, ${tauchgangszahl(ergebnis.bleibend.length)} ${bleibt} bei „${platz.name}".`;

      // Gefragt, nicht entschieden: Ob die Position zum wegziehenden Ort
      // gehört, weiss nur, wer dort getaucht ist.
      const angebot = dialog.querySelector('.aufteilleeren');
      angebot.hidden = !ergebnis.kannLeeren;
      angebot.querySelector('span').textContent =
        `Position${platz.region ? ' und Region' : ''} von „${platz.name}" leeren — `
        + `${koordinaten(platz.lat, platz.lon)}${platz.region ? `, ${platz.region}` : ''} `
        + 'gehört zum Ort, der wegzieht. Keiner der bleibenden Tauchgänge hat eigenes GPS.';
    }
    zeigeWirkung();
    dialog.addEventListener('change', zeigeWirkung);
    namensfeld.addEventListener('input', zeigeWirkung);

    // Die Entscheidung steht vor dem Schliessen fest, nicht im Empfänger —
    // `close()` kann das Ereignis synchron zustellen.
    let wahl = null;
    let erledigt = false;
    const beende = () => {
      if (erledigt) return;
      erledigt = true;
      dialog.remove();
      fertig(wahl);
    };
    dialog.addEventListener('close', beende);
    dialog.addEventListener('keydown', (e) => { if (e.key === 'Escape') beende(); });
    dialog.querySelector('.abbrechen').addEventListener('click', () => {
      dialog.close();
      beende();
    });
    knopf.addEventListener('click', () => {
      const angebot = dialog.querySelector('.aufteilleeren');
      wahl = {
        ids: gewaehlteIds(),
        name: namensfeld.value.trim(),
        leeren: !angebot.hidden && dialog.querySelector('.leeren').checked,
      };
      dialog.close();
      beende();
    });

    alsBlatt(dialog);
    dialog.showModal();
  });
}
